/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-case-declarations */

import {
  AfterViewInit,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { CompareHelper, ConvertHelper } from '@barthauer/abc/core';
import { AbcDateBoxComponent } from '@barthauer/abc/ui-components/abc-date-box';
import { AbcDropDownBoxComponent } from '@barthauer/abc/ui-components/abc-drop-down-box';
import { AbcTextBoxComponent } from '@barthauer/abc/ui-components/abc-text-box';
import dxCalendar from 'devextreme/ui/calendar';
import { ValueChangedEvent as DateBoxValuechangedEvent, OpenedEvent } from 'devextreme/ui/date_box';
import { ValueChangedEvent as DropDownBoxValuechangedEvent } from 'devextreme/ui/drop_down_box';
import { ValueChangedEvent as TextBoxValuechangedEvent } from 'devextreme/ui/text_box';

@Component({
  selector: 'abc-filter',
  styles: `
    .filter-box {
      width: 100px;
      height: 34px;
      margin: 0px 3px;
      background-color: #ffffffff !important;
      filter: drop-shadow(0px 3px 12px rgba(0, 0, 0, 0.1607843137));
      border-radius: 16px;
      border: none;

      &:first-child {
        margin-left: 0px;
      }
      &:last-child {
        margin-right: 0px;
      }
      ::ng-deep .transparent-caret {
        caret-color: transparent;
      }
    }
    :host ::ng-deep {
      display: flex;
      align-items: center;

      .dx-texteditor-input {
        min-height: 100% !important;
        font-size: 12px;
        font-weight: 500;
        color: rgba(0, 0, 0, 0.54);
        font-family: var(--DefaultFont, var(--app-font));
      }
      .dx-texteditor-buttons-container {
        width: 25px;
      }
      .dx-button-mode-text {
        background-color: transparent !important;
        width: 25px;
      }
      .dx-button-content {
        justify-content: flex-start;
      }
      .dx-dropdowneditor-icon {
        background-color: var(--PrimaryAccentColor, '#e2e2e2') !important;
        mask: url('assets/card-filter.svg') no-repeat center;
        mask-size: contain;
        width: 20px;
        height: 20px;

        &::before {
          content: none;
        }
      }
      .dx-texteditor.dx-state-focused .dx-texteditor-label,
      .dx-show-clear-button .dx-icon-clear {
        color: var(--PrimaryAccentColor, '#e2e2e2');
      }
      .no-icon {
        .dx-texteditor-buttons-container {
          width: 30px;
        }
      }
      .search {
        width: 160px;
        border-radius: 8px;
      }
    }
  `,
  template: `
    @for (filter of filters; track $index) {
      @switch (filter.type) {
        @case ('dropdownbox') {
          <abc-drop-down-box
            #dropDownBoxs
            [stylingMode]="'filled'"
            class="filter-box"
            [id]="filter.filterExpr + '|' + filter.type"
            [label]="filter.label"
            [inputAttr]="{ title: filter.tooltip ?? filter.label, class: 'transparent-caret' }"
            [activeStateEnabled]="false"
            [focusStateEnabled]="false"
            [showClearButton]="filter.showClearButton"
            [immediateValueChange]="true"
            (onValueChanged)="filerDataSource($event, $index)"
          >
          </abc-drop-down-box>
        }
        @case ('datebox') {
          <abc-date-box
            #dateBoxs
            [stylingMode]="'filled'"
            class="filter-box"
            [id]="filter.filterExpr + '|' + filter.type + '|' + filter.condition + '|' + filter.filterByYear"
            [label]="filter.label"
            [inputAttr]="{ title: filter.tooltip ?? filter.label }"
            [showClearButton]="filter.showClearButton"
            [displayFormat]="filter.filterByYear ? 'yyyy' : dateFilterFormat"
            [dropDownOptions]="
              filter.filterByYear
                ? {
                    wrapperAttr: {
                      id:
                        'calendar-' +
                        filter.filterExpr +
                        '|' +
                        filter.type +
                        '|' +
                        filter.condition +
                        '|' +
                        filter.filterByYear,
                    },
                  }
                : null
            "
            (onOpened)="filter.filterByYear ? filterByYearDateBoxHandler($event) : null"
            (onValueChanged)="filerDataSource($event, $index)"
          >
          </abc-date-box>
        }
        @case ('textbox') {
          <abc-text-box
            #textBoxs
            [stylingMode]="'filled'"
            class="filter-box no-icon"
            [class.search]="filter.filterByFields"
            [id]="filter.filterExpr + '|' + filter.type + '|' + '|' + '|' + JSON.stringify(filter.filterByFields)"
            [label]="filter.filterByFields ? '' : filter.label"
            [inputAttr]="{ title: filter.tooltip ?? filter.label }"
            [placeholder]="filter.filterByFields ? filter.label : ''"
            [mode]="'search'"
            [showClearButton]="filter.showClearButton"
            (onValueChanged)="filerDataSource($event, $index)"
            [valueChangeEvent]="'keyup'"
          >
          </abc-text-box>
        }
      }
    }
  `,
  imports: [forwardRef(() => AbcDropDownBoxComponent), AbcDateBoxComponent, AbcTextBoxComponent],
})
export class AbcFilterComponent implements AfterViewInit, OnDestroy {
  JSON = JSON;
  @ViewChildren('dropDownBoxs') dropDownBoxs: QueryList<AbcDropDownBoxComponent>;
  @ViewChildren('dateBoxs') dateBoxs: QueryList<AbcDateBoxComponent>;
  @ViewChildren('textBoxs') textBoxs: QueryList<AbcTextBoxComponent>;

  @Input() filters: Filter[] = [];
  @Input('dataSource') set data(value: any[]) {
    this.dataSource = value;
    if (!this._dataSourceBackup.length) {
      this._dataSourceBackup = value;
    }
  }

  @Output() dataSourceChange: EventEmitter<any[]> = new EventEmitter<any[]>();
  @Output() onFiltering: EventEmitter<FilteringEvent> = new EventEmitter<FilteringEvent>();

  private dataSource: any[] = [];
  private _dataSourceBackup: any[] = [];
  // the global filter structure that dataSource will be filtered by
  private _filterValue: FilterMap = {};

  protected readonly dateFilterFormat = (date: Date) => ConvertHelper.toLocaleFormattedDateString(date);

  public get filterValue(): FilterMap {
    return this._filterValue;
  }
  public get isFiltered(): boolean {
    return this.dataSource.length !== this.dataSourceBackup.length;
  }
  public get dataSourceBackup(): any[] {
    return this._dataSourceBackup;
  }

  constructor() {}

  ngAfterViewInit(): void {
    // setting each filter dropdownbox values
    this.dropDownBoxs.toArray().forEach((dropDownBox: AbcDropDownBoxComponent, index: number) => {
      const filter = this.filters.filter((_) => _.type === FilterType.dropdownbox)[index];

      setTimeout(() => {
        dropDownBox.setupForArrayData(
          filter.options ?? [],
          'value',
          'caption',
          [{ dataField: 'caption' }],
          filter.initialValue,
          filter.useMultiSelect,
        );
      }, 0);

      setTimeout(() => {
        dropDownBox.abcGrid.showColumnHeaders = false;
        dropDownBox.abcGrid.filterRow = { visible: !!filter.filterOptions };
      }, 0);
    });

    // setting each filter datebox values
    this.dateBoxs.toArray().forEach((dateBox: AbcDateBoxComponent, index: number) => {
      const filters = this.filters.filter((_) => _.type === FilterType.datebox);

      setTimeout(() => {
        dateBox.value = filters[index].initialValue;
      }, 0);
    });

    // setting each filter textbox values
    this.textBoxs.toArray().forEach((textBox: AbcTextBoxComponent, index: number) => {
      const filters = this.filters.filter((_) => _.type === FilterType.textbox);

      setTimeout(() => {
        textBox.value = filters[index].initialValue;
      }, 0);
    });
  }

  ngOnDestroy(): void {
    // restore the dataSource for the parent
    this._filterValue = {};
    this.dataSource = this.dataSourceBackup;
    this.dataSourceChange.emit(this._dataSourceBackup);
  }

  public filterByYearDateBoxHandler(e: OpenedEvent): void {
    const container = document.getElementById('calendar-' + e.component.element().id);
    const calendar = container.querySelector('.dx-calendar');

    if (calendar) {
      const calendarInstance = dxCalendar.getInstance?.(calendar);
      calendarInstance.option('maxZoomLevel', 'decade');
    }
  }

  public filerDataSource(
    e: DropDownBoxValuechangedEvent | DateBoxValuechangedEvent | TextBoxValuechangedEvent,
    index: number,
  ): void {
    // getting the filter attributes
    const filterAttributes = e.element.id.split('|');

    const filterExpr = filterAttributes[0];
    const filterType = filterAttributes[1] as FilterType;
    const filterCondition = filterAttributes.length > 2 ? (filterAttributes[2] as DateFilterCondition) : null;
    const filterByYear = filterAttributes.length > 3 ? ConvertHelper.toBoolean(filterAttributes[3]) : false;
    const filterByFields = filterAttributes.length > 4 ? this.JSON.parse(filterAttributes[4]) : null;
    //

    // global filter construction
    let valueBackup: string | number | Date | (string | number)[];
    let value: string | number | Date | (string | number)[];

    switch (filterType) {
      case FilterType.dropdownbox:
        value = e.value !== null ? this.prepareDropDownBoxFilterValue(index, e.value) : [''];
        valueBackup = e.value ? e.value : [''];
        break;
      case FilterType.datebox:
        value = e.value ? e.value : '';
        valueBackup = value;
        break;
      case FilterType.textbox:
        value = e.value ? e.value : '';
        valueBackup = value;
        break;
    }

    this._filterValue[filterExpr] = { filterType: filterType, value: value };

    if (filterCondition) {
      this._filterValue[filterExpr]['filterCondition'] = filterCondition;
    }

    if (filterByYear) {
      this._filterValue[filterExpr]['filterByYear'] = filterByYear;
    }

    if (filterByFields) {
      this._filterValue[filterExpr]['filterByFields'] = filterByFields;
    }
    //

    // applying the filter
    const handlers = Object.entries(this._filterValue).map(([expr, record]) => ({
      expr,
      predicate: this.filterHandler(record),
    }));

    this.dataSource = this._dataSourceBackup.filter((row: any) =>
      handlers.every(({ expr, predicate }) => predicate(row, expr)),
    );
    //

    // event emitting
    const filteringEvent = new FilteringEvent({
      index: index,
      value: valueBackup,
      filter: this.filters[index],
      instance: this.dropDownBoxs[index],
    });

    this.dataSourceChange.emit(this.dataSource);
    this.onFiltering.emit(filteringEvent);
    //
  }

  // extract dropdownBoxFilter multiOptionValues
  private prepareDropDownBoxFilterValue(filterIndex: number, filterValue: any): string[] {
    const result: string[] = [];
    const filter = this.filters[filterIndex] as DropdownFilter;
    const seperator = filter.multiOptionValueSeperator;
    const value = Array.isArray(filterValue) ? filterValue : [filterValue];

    if (seperator) {
      for (const __ of value) {
        if (__.toString().includes(seperator)) {
          __.split(seperator).map((_: string) => result.push(_));
        } else {
          result.push(__.toString());
        }
      }
      return result;
    } else {
      return value;
    }
  }
  //

  // the filtering mechanism to apply based on the filterRecord => filterType
  private filterHandler(filterRecord: FilterRecord): (row: any, expr: string) => boolean {
    const { filterType, value } = filterRecord;

    switch (filterType) {
      case FilterType.dropdownbox:
        return (row: any, expr: string) =>
          Array.isArray(value) &&
          value.some(
            (entry: any) =>
              (typeof entry === 'number' ? entry === row[expr] : row[expr]?.toString().includes(entry?.toString())) ||
              entry === '',
          );

      case FilterType.datebox: {
        const dateValue = value as Date;
        const condition = filterRecord.filterCondition;
        const filterByYear = filterRecord.filterByYear;

        if (value) {
          return (row: any, expr: string) => {
            const rawRowDateValue = ConvertHelper.toDate(ConvertHelper.toLocaleFormattedDateString(row[expr]));
            const rawFilterDateValue = ConvertHelper.toDate(ConvertHelper.toLocaleFormattedDateString(dateValue));

            const rowDate = filterByYear ? rawRowDateValue.getFullYear() : rawRowDateValue;
            const filterDate = filterByYear ? rawFilterDateValue.getFullYear() : rawFilterDateValue;

            switch (condition) {
              case DateFilterCondition.eq:
                return CompareHelper.areDeepEqual(rowDate, filterDate);
              case DateFilterCondition.gt:
                return rowDate > filterDate;
              case DateFilterCondition.lt:
                return rowDate < filterDate;
              case DateFilterCondition.gte:
                return rowDate >= filterDate;
              case DateFilterCondition.lte:
                return rowDate <= filterDate;
              default:
                return true;
            }
          };
        } else {
          return () => true;
        }
      }

      case FilterType.textbox:
        const filterByFields = filterRecord.filterByFields;

        return (row: any, expr: string) => {
          if (value && typeof value === 'string') {
            if (filterByFields?.length) {
              return filterByFields.some((key: string) =>
                row[key]?.toString().toLowerCase().includes(value.toLowerCase()),
              );
            } else {
              return row[expr]?.toString().toLowerCase().includes(value.toLowerCase());
            }
          } else {
            return () => true;
          }
        };

      default:
        return () => true;
    }
  }
}

class BaseFilter {
  label?: string;
  tooltip?: string;
  showClearButton?: boolean;
  filterExpr: string;
}

class DropdownFilter extends BaseFilter {
  type: FilterType.dropdownbox;
  options?: FilterOption[];
  initialValue?: (string | number)[];
  useMultiSelect?: boolean;
  multiOptionValueSeperator?: string;
  filterOptions?: boolean;
}

class DateboxFilter extends BaseFilter {
  type: FilterType.datebox;
  condition: DateFilterCondition;
  filterByYear?: boolean;
  initialValue?: string | number | Date;
}

class TextboxFilter extends BaseFilter {
  type: FilterType.textbox;
  initialValue?: string;
  filterByFields?: string[];
}

export type Filter = DropdownFilter | DateboxFilter | TextboxFilter;

export enum FilterType {
  dropdownbox = 'dropdownbox',
  datebox = 'datebox',
  textbox = 'textbox',
}

export enum DateFilterCondition {
  // lower than
  lt = 'lt',
  // greater than
  gt = 'gt',
  // lower than or equals
  lte = 'lte',
  // greater than or equals
  gte = 'gte',
  // equals
  eq = 'eq',
}

export class FilterOption {
  caption: string;
  value: string | number;
}

export class FilteringEvent {
  readonly index: number;
  readonly value: string | number | Date | (string | number)[];
  readonly filter: Filter;
  readonly instance: AbcDropDownBoxComponent | AbcDateBoxComponent | AbcTextBoxComponent;

  constructor(data: FilteringEvent) {
    Object.assign(this, data);
  }
}

type FilterRecord = {
  filterType: FilterType;
  value: Date | string | number | (string | number)[];
  filterCondition?: DateFilterCondition;
  filterByYear?: boolean;
  filterByFields?: string[];
};

type FilterMap = {
  [key: string]: FilterRecord;
};
